import { Component, signal, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AudienceDashboardAdapterService } from '../../services/audience-engine-adapter.service';
import { AudienceDashboardService } from '../../services/audience-engine.service';
import { SharedDataService } from '../../services/shared-data.service';
import { Chart, registerables } from 'chart.js';
import { FilterService } from '../../services/filter.service';
import { AuthService } from '../../services/auth.service';
import { Subscription, forkJoin, skip, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

Chart.register(...registerables);

type DateRangeOption = '7days' | '14days' | '30days' | '90days' | 'year';
type DeviceMetricId = 'device' | 'source' | 'goal';

interface DeviceMetricSlice {
  label: string;
  value: number;
  raw: number;
  color: string;
  tooltip: string;
}

@Component({
  selector: 'app-public',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './public.html',
  styleUrl: '../../app.scss'
})
export class PublicComponent implements OnInit, AfterViewInit, OnDestroy {
  isTableView = false;
  expandedRows: any[] = [];
  private filterSubscription: Subscription = new Subscription();
  isLoading = false;
  dataError = false;
  dataErrorMessage = '';
  hasFatalError = false;
  canRetry = true;

  // Cache for original API table data (to enable proper aggregation on column toggle)
  private cachedTableData: any[] = [];

  // Device chart metric selector
  selectedDeviceMetric: DeviceMetricId = 'device'; // default to device percentage
  availableDeviceMetrics: Array<{ id: DeviceMetricId; name: string }> = [
    { id: 'device', name: 'dashboard.deviceMetrics.device' },
    { id: 'source', name: 'dashboard.deviceMetrics.source' },
    { id: 'goal', name: 'dashboard.deviceMetrics.goal' }
  ];

  // Column visibility controls
  columnVisibility = {
    audience: true,
    source: true,
    device: true,
    users: true,
    goal: true
  };
  private readonly selectableColumns: Array<'source' | 'device' | 'goal'> = ['source', 'device', 'goal'];

  showColumnSelector = false;
  private usersDeviceDataByMetric: Record<DeviceMetricId, DeviceMetricSlice[]> = {
    device: [],
    source: [],
    goal: []
  };
  usersDeviceLegend: Array<{ label: string; color: string }> = [];
  audienceCards: Array<{ name: string; value: string; percentage: string; color: string; textColor: string }> = [];

  totalUsersHasData = true;
  treemapHasData = true;

  private filterSubscriptionsInitialized = false;
  private totalUsersChartInstance: Chart | null = null;
  private usersCategoryChartInstance: Chart | null = null;
  private usersDeviceChartInstance: Chart | null = null;
  private usersAudienceChartInstance: Chart | null = null;

  private treemapColorPalette = [
    '#1e3a8a', '#3b82f6', '#fbbf24', '#f97316', '#8b5cf6',
    '#ec4899', '#10b981', '#14b8a6', '#f59e0b', '#ef4444'
  ];
  private readonly metricPalette = [
    '#1e3a8a', '#3b82f6', '#f97316', '#fbbf24', '#10b981',
    '#8b5cf6', '#ec4899', '#14b8a6', '#ef4444', '#6366f1'
  ];

  constructor(
    private filterService: FilterService,
    private audienceDashboardAdapter: AudienceDashboardAdapterService,
    private audienceDashboardService: AudienceDashboardService,
    private sharedDataService: SharedDataService,
    private translateService: TranslateService,
    private authService: AuthService
  ) {}

  private destroyDashboardCharts(): void {
    this.totalUsersChartInstance?.destroy();
    this.totalUsersChartInstance = null;
    this.usersCategoryChartInstance?.destroy();
    this.usersCategoryChartInstance = null;
    this.usersDeviceChartInstance?.destroy();
    this.usersDeviceChartInstance = null;
    this.usersAudienceChartInstance?.destroy();
    this.usersAudienceChartInstance = null;
  }

  private handleDataError(message: string, canRetry = true, fatal = false): void {
    console.error('[PUBLIC] Dashboard data unavailable:', message);
    this.dataError = true;
    this.dataErrorMessage = message;
    this.canRetry = canRetry;
    this.hasFatalError = true;
    this.isLoading = false;
    this.sharedDataService.setLoading('public', false);
    this.destroyDashboardCharts();
    this.usersDeviceDataByMetric = { device: [], source: [], goal: [] };
    this.usersDeviceLegend = [];
  }

  retryLoad(): void {
    if (this.isLoading) {
      return;
    }
    this.dataError = false;
    this.dataErrorMessage = '';
    this.sharedDataService.setLoading('public', true);
    this.initializeCharts();
  }

  toggleTableView() {
    this.isTableView = !this.isTableView;
    if (this.isTableView) {
      this.columnVisibility.audience = true;
      this.columnVisibility.users = true;
      this.updateExpandedRows();
    } else {
      // Reinitialize charts when returning to chart view
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleColumnSelector() {
    this.showColumnSelector = !this.showColumnSelector;
  }

  onColumnVisibilityChange() {
    this.updateExpandedRows();
  }

  toggleColumn(column: string) {
    if (!this.selectableColumns.includes(column as 'source' | 'device' | 'goal')) {
      return;
    }
    this.columnVisibility[column as keyof typeof this.columnVisibility] = !this.columnVisibility[column as keyof typeof this.columnVisibility];
    // Recalculate table data when column visibility changes
    this.updateExpandedRows();
  }

  selectAllColumns() {
    this.selectableColumns.forEach(column => {
      this.columnVisibility[column] = true;
    });
    this.columnVisibility.audience = true;
    this.columnVisibility.users = true;
    this.updateExpandedRows();
  }

  areAllColumnsSelected(): boolean {
    return this.selectableColumns.every(column => this.columnVisibility[column]);
  }

  // Device metric selector methods
  onDeviceMetricChange(metricId: DeviceMetricId) {
    this.selectedDeviceMetric = metricId;
    // Regenerate device chart with new metric
    this.createUsersDeviceChart();
  }

  get selectedDeviceMetricLabel(): string {
    const metric = this.availableDeviceMetrics.find(item => item.id === this.selectedDeviceMetric);
    return metric?.name || 'dashboard.deviceMetrics.device';
  }

  // Helper methods for device chart
  private getDeviceColor(device: string): string {
    const colorMap: { [key: string]: string } = {
      'Mobile': '#1e40af',
      'Desktop': '#3b82f6',
      'Tablet': '#93c5fd',
      'Smart TV': '#60a5fa',
      'Wearable': '#dbeafe'
    };
    return colorMap[device] || '#94a3b8';
  }

  // Get audiences from FilterService
  get availableAudiences() {
    return this.filterService.getAvailableAudiences();
  }

  // Computed property for selected audiences
  get selectedAudiences() {
    return this.filterService.getSelectedAudiences();
  }

  updateExpandedRows() {

    const showTableSpinner = this.isTableView;
    if (showTableSpinner) {
      this.isLoading = true;
      this.sharedDataService.setLoading('public', true);
    }

    // Prepare visible columns for backend
    const visibleColumns = Object.keys(this.columnVisibility).filter((key)=>key!=='users' && !!(this.columnVisibility as any)[key]);

    // Build FilterColumns from selected device, source, and goal
    const selectedDevices = this.filterService.getSelectedDevices().map(d => d.id);
    const selectedSources = this.filterService.getSelectedTrafficSources().map(s => s.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    const filterColumns: string[] = [
      ...selectedDevices,
      ...selectedSources,
      ...(selectedGoal ? [selectedGoal.id] : [])
    ];


    // Always load from API with current column visibility and filters (no cache to ensure correct aggregation)
    this.audienceDashboardAdapter.getTableData(visibleColumns, filterColumns).subscribe({
      next: (allTableData) => {
        this.processTableData(allTableData);
        if (showTableSpinner) {
          this.isLoading = false;
          this.sharedDataService.setLoading('public', false);
        }
      },
      error: (error) => {
        console.error('[PUBLIC] Error loading table data:', error);
        this.expandedRows = [];
        if (showTableSpinner) {
          this.isLoading = false;
          this.sharedDataService.setLoading('public', false);
        }
      }
    });
  }

  private processTableData(allTableData: any[]) {
    // Backend already filters by device/source/goal based on sidebar selections
    // We only need to filter by audience for VISUALIZATION (which audiences to show in table)
    const selectedAudiences = this.filterService.getSelectedAudiences().map(a => a.id);


    // Filter ONLY by audience if column is visible (audience is for visualization, not DB filtering)
    const filteredData = !this.columnVisibility.audience
      ? allTableData  // If audience column hidden, show all
      : allTableData.filter(row => selectedAudiences.includes(row.audience_id));


    const aggregatedData = this.aggregateTableRows(filteredData);

    // Group by audience_id to show audience name only once
    const groupedByAudience: { [key: string]: any[] } = {};
    aggregatedData.forEach(row => {
      const audienceKey = this.columnVisibility.audience ? row.audience_id : 'all';
      if (!groupedByAudience[audienceKey]) {
        groupedByAudience[audienceKey] = [];
      }
      groupedByAudience[audienceKey].push(row);
    });

    // Build expandedRows array
    this.expandedRows = [];
    const allUserCounts = aggregatedData.map(row => row.user_count);
    const maxUsers = allUserCounts.length > 0 ? Math.max(...allUserCounts) : 1;

    Object.keys(groupedByAudience).forEach(audienceId => {
      const rows = groupedByAudience[audienceId];
      rows.forEach((row, index) => {
        this.expandedRows.push({
          audience: this.columnVisibility.audience && index === 0 ? row.audience_name : '', // Show audience name only in first row
          source: this.columnVisibility.source ? row.source_name : '-',
          device: this.columnVisibility.device ? row.device_name : '-',
          goal: this.columnVisibility.goal ? row.goal_name : '-',
          userCount: Number(row.user_count) || 0,
          userProgress: maxUsers > 0 ? (Number(row.user_count) / maxUsers) * 100 : 0
        });
      });
    });

  }

  /**
   * Aggrega i dati della tabella in base alle colonne visibili.
   * Quando una colonna è nascosta, i valori vengono sommati per evitare duplicati.
   */
  private aggregateTableRows(rows: any[]): any[] {
    if (!rows || rows.length === 0) {
      return [];
    }

    const groupedRows = new Map<string, any>();

    rows.forEach(rawRow => {
      const normalizedRow = {
        ...rawRow,
        audience_id: rawRow.audience_id ?? rawRow.audienceId ?? 'unknown_audience',
        audience_name: rawRow.audience_name ?? rawRow.audience ?? 'Unknown Audience',
        source_id: this.columnVisibility.source ? (rawRow.source_id ?? 'unknown_source') : '__all__',
        source_name: this.columnVisibility.source
          ? (rawRow.source_name ?? rawRow.source ?? 'Unknown Source')
          : 'All Sources',
        device_id: this.columnVisibility.device ? (rawRow.device_id ?? 'unknown_device') : '__all__',
        device_name: this.columnVisibility.device
          ? (rawRow.device_name ?? rawRow.device ?? 'Unknown Device')
          : 'All Devices',
        goal_id: this.columnVisibility.goal ? (rawRow.goal_id ?? 'unknown_goal') : '__all__',
        goal_name: this.columnVisibility.goal
          ? (rawRow.goal_name ?? rawRow.goal ?? 'Unknown Goal')
          : 'All Goals'
      };

      const key = [
        normalizedRow.audience_id,
        normalizedRow.source_id,
        normalizedRow.device_id,
        normalizedRow.goal_id
      ].join('|');

      const rowCount = Number(rawRow.user_count ?? rawRow.userCount) || 0;

      if (groupedRows.has(key)) {
        const existing = groupedRows.get(key);
        existing.user_count = (Number(existing.user_count) || 0) + rowCount;
      } else {
        groupedRows.set(key, {
          ...normalizedRow,
          user_count: rowCount
        });
      }
    });

    return Array.from(groupedRows.values());
  }

  // Reinitialize charts when selection changes
  reinitializeCharts() {
    if (!this.isTableView) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  ngOnInit() {

    // Set loading state immediately and notify SharedDataService
    this.resetErrorState();
    this.isLoading = true;
    this.sharedDataService.setLoading('public', true);

    // Switch filter context to public audiences
    this.filterService.switchAudienceContext('public');

    // NOTE: Removed updateExpandedRows() from here to avoid duplicate API calls
    // It will be called by initializeCharts() in ngAfterViewInit

    // Wait for filters to be loaded BEFORE subscribing to filter changes
    // This prevents the initial API-loaded values from triggering subscriptions
    this.filterSubscription.add(
      this.filterService.filtersLoaded$.subscribe(loaded => {
        if (!loaded) {
          return;
        }
        if (!this.filterSubscriptionsInitialized) {
          this.filterSubscriptionsInitialized = true;
          this.subscribeToFilterChanges();
        } else {
          this.reinitializeCharts();
        }
      })
    );
  }

  private subscribeToFilterChanges() {
    // Subscribe to filter changes - skip initial emission to avoid duplicate calls
    this.filterSubscription.add(
      this.filterService.selectedAudiences$.pipe(
        skip(1) // Skip the current value emission on subscription
      ).subscribe(() => {
        this.cachedTableData = []; // Clear cache when audiences change
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );

    // Subscribe to date range changes
    this.filterSubscription.add(
      this.filterService.selectedDateRange$.pipe(
        skip(1) // Skip the current value emission on subscription
      ).subscribe(() => {
        this.reinitializeCharts();
      })
    );

    // Subscribe to device filter changes
    this.filterSubscription.add(
      this.filterService.selectedDevices$.pipe(
        skip(1) // Skip the current value emission on subscription
      ).subscribe(() => {
        this.cachedTableData = []; // Clear cache when devices change
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );

    // Subscribe to traffic source filter changes
    this.filterSubscription.add(
      this.filterService.selectedTrafficSources$.pipe(
        skip(1) // Skip the current value emission on subscription
      ).subscribe(() => {
        this.cachedTableData = []; // Clear cache when sources change
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );

    // Subscribe to goal filter changes
    this.filterSubscription.add(
      this.filterService.selectedGoal$.pipe(
        skip(1) // Skip the current value emission on subscription
      ).subscribe(() => {
        this.cachedTableData = []; // Clear cache when goal changes
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );

    // Subscribe to toggle changes for applying filters to primary charts
    this.filterSubscription.add(
      this.filterService.applyFiltersToAllCharts$.pipe(
        skip(1)
      ).subscribe(() => {
        this.cachedTableData = [];
        if (this.isTableView) {
          this.updateExpandedRows();
        }
        this.reinitializeCharts();
      })
    );
  }

  ngOnDestroy() {
    this.filterSubscription.unsubscribe();
    this.destroyDashboardCharts();
  }

  ngAfterViewInit() {

    // IMPORTANT: Aspetta che i filtri siano caricati PRIMA di inizializzare i grafici
    // Questo garantisce che il DaysBack calcolato sia basato sui dati disponibili
    this.filterService.filtersLoaded$.pipe(
      filter(loaded => loaded === true),
      take(1)
    ).subscribe(() => {
      // Small delay to ensure DOM is fully ready
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    });
  }


  private initializeCharts() {

    if (!this.authService.currentDcrHasVisionAccess()) return;

    this.resetErrorState();

    // Show loading spinner
    this.isLoading = true;

    // Destroy existing charts to prevent canvas reuse issues
    this.destroyDashboardCharts();

    // Prepare filters based on current selection
    const selectedAudiences = this.selectedAudiences;
    const dateRange = this.filterService.getSelectedDateRange();

    // Mappa periodi in giorni
    const daysBackMap: { [key: string]: number } = {
      '7days': 7,
      '14days': 14,
      '30days': 30,
      '90days': 90,
      'year': 365
    };
    const daysBack = daysBackMap[dateRange] || 30;

    // Prova prima con le nuove API Audience Dashboard
    this.loadChartsWithAudienceDashboardAPI(selectedAudiences, daysBack, dateRange);
  }

  private loadChartsWithAudienceDashboardAPI(selectedAudiences: any[], daysBack: number, dateRange: DateRangeOption) {
    const audienceIds = selectedAudiences.map(a => a.id);
    const audienceColors = selectedAudiences.reduce((acc: any, aud: any) => {
      acc[aud.id] = aud.color;
      return acc;
    }, {});

    this.audienceDashboardAdapter.setDaysBack(daysBack);

    // OPTIMIZATION: Load shared data (totals + daily) ONCE before processing
    // Filters are already loaded by FilterService, so we reuse them

    const filters = this.filterService.getAllFilters();

    // Build FilterColumns from selected device, source, and goal
    const selectedDevices = this.filterService.getSelectedDevices().map(d => d.id);
    const selectedSources = this.filterService.getSelectedTrafficSources().map(s => s.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    const filterColumns: string[] = [
      ...selectedDevices,
      ...selectedSources,
      ...(selectedGoal ? [selectedGoal.id] : [])
    ];


    // Audience columns are used ONLY for visualization (which lines to show in charts)
    const audienceColumns = filters
      .filter(f => f.ObjectType === 'audience' && audienceIds.includes(f.ColumnName))
      .map(f => f.ColumnName);


    const applyFiltersToPrimaryCharts = this.filterService.getApplyFiltersToAllCharts();
    const baseFilterColumns = applyFiltersToPrimaryCharts ? filterColumns : [];

    const totalsFiltered$ = this.audienceDashboardService.getChartTotals(daysBack, filterColumns);
    const audienceDailyFiltered$ = this.audienceDashboardService.getChartDailyTrend(daysBack, filterColumns, audienceColumns);
    const baseTotals$ = this.audienceDashboardService.getChartTotals(daysBack, baseFilterColumns);
    const baseDaily$ = this.audienceDashboardService.getChartDailyTrend(daysBack, baseFilterColumns, []);
    const comparisonDaysBack = this.getComparisonDaysBack(dateRange, daysBack);
    const comparisonDaily$ = comparisonDaysBack > daysBack
      ? this.audienceDashboardService.getChartDailyTrend(comparisonDaysBack, baseFilterColumns, [])
      : of(null);

    forkJoin({
      filteredTotals: totalsFiltered$,
      filteredDaily: audienceDailyFiltered$,
      baseTotals: baseTotals$,
      baseDaily: baseDaily$,
      comparisonDaily: comparisonDaily$
    }).subscribe({
      next: (sharedData) => {
        this.resetErrorState();
        if (!this.hasFilteredData(sharedData, filterColumns)) {
          const noDataMessage = this.translateService.instant('dashboard.status.noDataFilters');
          this.handleDataError(noDataMessage, false, true);
          return;
        }

        try {
          const totalUsersData = this.processTotalUsersData(
            sharedData.baseTotals,
            sharedData.baseDaily,
            dateRange,
            sharedData.comparisonDaily
          );
          const usersCategoryData = this.processUsersCategoryData(sharedData.baseTotals, filters);
          const usersDeviceDataByMetric = this.processUsersDeviceData(sharedData.baseTotals, filters);
          const treemapData = this.processTreemapData(sharedData.filteredTotals, filters, audienceIds, audienceColors);
          const audiencePublicData = this.processAudiencePublicData(sharedData.filteredDaily, audienceColumns);

          // Create all charts
          this.createTotalUsersChart(totalUsersData.chartData);
          this.updateTotalUsersDisplay(totalUsersData);
          this.createUsersCategoryChart(usersCategoryData);
          this.usersDeviceDataByMetric = usersDeviceDataByMetric;
          this.createUsersDeviceChart();
          this.createUsersAudienceChart(audiencePublicData);
          this.updateTreemapData(treemapData || []);

          // Store data in SharedDataService for Personas to reuse
          this.sharedDataService.setPublicData({
            totalUsers: totalUsersData,
            usersCategory: usersCategoryData,
            usersDevice: usersDeviceDataByMetric,
            audiencePublic: audiencePublicData,
            audienceTreemap: treemapData,
            filters: {
              audienceColumns,
              daysBack,
              selectedAudiences,
              audienceIds,
              audienceColors
            }
          });

          // Hide loading ONLY after ALL charts are ready
          this.resetErrorState();
          this.isLoading = false;
          this.sharedDataService.setLoading('public', false);
        } catch (processingError) {
          console.error('Error processing dashboard data:', processingError);
          this.handleDataError('Impossibile elaborare i dati della dashboard.', true, true);
        }
      },
      error: (error) => {
        console.error('Error loading shared dashboard data:', error);
        this.handleDataError('Impossibile recuperare i dati della dashboard.', true, true);
      }
    });
  }

  private resetErrorState(): void {
    if (this.dataError || this.hasFatalError) {
      this.dataError = false;
      this.dataErrorMessage = '';
      this.hasFatalError = false;
      this.canRetry = true;
    }
  }

  private hasFilteredData(sharedData: {
    filteredTotals: any;
    filteredDaily: any;
    baseTotals: any;
    baseDaily: any;
  }, filterColumns: string[]): boolean {
    const filteredTotalsHasData = this.responseHasPositiveAggregates(sharedData.filteredTotals, filterColumns);
    const filteredDailyHasData = this.dailyResponseHasValues(sharedData.filteredDaily);

    if (filterColumns.length > 0) {
      return filteredTotalsHasData || filteredDailyHasData;
    }

    return (
      filteredTotalsHasData ||
      filteredDailyHasData ||
      this.responseHasPositiveValues(sharedData.baseTotals) ||
      this.dailyResponseHasValues(sharedData.baseDaily)
    );
  }

  private responseHasPositiveValues(response: any): boolean {
    if (!response || response.error) {
      return false;
    }
    const payload: Array<Record<string, unknown>> = Array.isArray(response.payload) ? response.payload : [];
    if (payload.length === 0) {
      return false;
    }
    return payload.some((entry: Record<string, unknown>) =>
      Object.values(entry || {}).some((value: unknown) => {
        if (typeof value === 'number') {
          return value > 0;
        }
        const numeric = Number(value);
        return !isNaN(numeric) && numeric > 0;
      })
    );
  }

  private responseHasPositiveAggregates(response: any, filterColumns: string[]): boolean {
    if (!response || response.error) {
      return false;
    }
    const payload: Array<Record<string, unknown>> = Array.isArray(response.payload) ? response.payload : [];
    if (payload.length === 0) {
      return false;
    }
    return payload.some((entry: Record<string, unknown>) => {
      const tncids = Number(entry?.['TNCIDs'] ?? entry?.['tncids'] ?? 0);
      if (tncids > 0) {
        return true;
      }
      if (filterColumns.length === 0) {
        return false;
      }
      return filterColumns.some(column => {
        const value = Number(entry?.[column] ?? 0);
        return value > 0;
      });
    });
  }

  private dailyResponseHasValues(response: any): boolean {
    if (!response || response.error) {
      return false;
    }
    const payload: Array<Record<string, unknown>> = Array.isArray(response.payload) ? response.payload : [];
    if (payload.length === 0) {
      return false;
    }
    return payload.some((entry: Record<string, unknown>) => {
      const value = Number(entry?.['TNCIDs'] ?? entry?.['tncids'] ?? 0);
      return value > 0;
    });
  }

  // Process methods to transform raw API data locally (no additional HTTP calls)
  private getComparisonDaysBack(range: DateRangeOption, defaultDaysBack: number): number {
    switch (range) {
      case '7days':
        return 30;
      case '14days':
        return 30;
      default:
        return defaultDaysBack;
    }
  }

  private getWindowSizeForRange(range: DateRangeOption): number | null {
    switch (range) {
      case '7days':
        return 7;
      case '14days':
        return 14;
      default:
        return null;
    }
  }

  private sumTncIds(entries: any[]): number {
    return entries.reduce((sum, item) => sum + (Number(item?.['TNCIDs']) || 0), 0);
  }

  private sortDailyEntries(entries: any[]): any[] {
    return [...(entries || [])].sort((a, b) => {
      const dateA = new Date(a?.Date || a?.date || 0).getTime();
      const dateB = new Date(b?.Date || b?.date || 0).getTime();
      return dateA - dateB;
    });
  }

  // Process methods to transform raw API data locally (no additional HTTP calls)
  private processTotalUsersData(
    totalsResponse: any,
    dailyResponse: any,
    selectedRange: DateRangeOption,
    comparisonDailyResponse?: any
  ): any {
    if (totalsResponse.error || dailyResponse.error || comparisonDailyResponse?.error) {
      throw new Error(
        totalsResponse.error?.message ||
        dailyResponse.error?.message ||
        comparisonDailyResponse?.error?.message
      );
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const totalsRow = totalsPayload[0] || {};
    const totalUsers = parseInt((totalsRow?.['TNCIDs'] as string) || '0', 10) || 0;
    const dailyData = this.sortDailyEntries(Array.isArray(dailyResponse.payload) ? dailyResponse.payload : []);
    const comparisonDataRaw = Array.isArray(comparisonDailyResponse?.payload)
      ? comparisonDailyResponse.payload
      : dailyData;
    const comparisonData = this.sortDailyEntries(comparisonDataRaw);

    const windowSize = this.getWindowSizeForRange(selectedRange);
    let percentage = '';
    let growthType: 'positive' | 'negative' | 'neutral' = 'neutral';

    if (selectedRange !== '30days' && windowSize && comparisonData.length > windowSize) {
      const recentSlice = comparisonData.slice(-windowSize);
      let previousSlice = comparisonData.slice(-(windowSize * 2), -windowSize);

      if (previousSlice.length === 0) {
        previousSlice = comparisonData.slice(0, Math.max(0, comparisonData.length - windowSize));
      }

      const recentTotal = this.sumTncIds(recentSlice);
      const previousTotal = this.sumTncIds(previousSlice);

      const recentAverage = recentSlice.length > 0 ? recentTotal / recentSlice.length : 0;
      const previousAverage = previousSlice.length > 0 ? previousTotal / previousSlice.length : 0;

      if (previousAverage > 0) {
        const growth = ((recentAverage - previousAverage) / previousAverage) * 100;
        if (Math.abs(growth) < 0.0001) {
          percentage = '0.0%';
          growthType = 'neutral';
        } else {
          growthType = growth >= 0 ? 'positive' : 'negative';
          percentage = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`;
        }
      }
    }

    const chartData = dailyData.map((d: any) => ({
      date: this.formatDate(d.Date),
      users: d?.['TNCIDs']
    }));

    this.totalUsersHasData = chartData.length > 0 || totalUsers > 0;

    return {
      value: this.formatNumber(totalUsers),
      percentage,
      growthType,
      chartData
    };
  }

  private processUsersCategoryData(totalsResponse: any, filters: any[]): any[] {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const data = totalsPayload[0] || {};
    const totalUsers = parseInt((data?.['TNCIDs'] as string) || '0', 10) || 0;

    const enrichedColumn = filters.find(f =>
      f.ObjectType?.toLowerCase() === 'enriched' &&
      f.ObjectName?.toLowerCase() === 'clusters'
    );

    let clusterUsers = 0;
    if (enrichedColumn) {
      clusterUsers = parseInt(data[enrichedColumn.ColumnName] || '0');
    }

    const nullUsers = totalUsers - clusterUsers;
    const clusterPerc = totalUsers > 0 ? Math.round((clusterUsers / totalUsers) * 100) : 0;
    const nullPerc = totalUsers > 0 ? Math.round((nullUsers / totalUsers) * 100) : 0;

    return [
      {
        category: 'YES',
        percentage: clusterPerc,
        value: `${this.formatNumber(clusterUsers)} | ${clusterPerc}%`
      },
      {
        category: 'NO',
        percentage: nullPerc,
        value: `${this.formatNumber(nullUsers)} | ${nullPerc}%`
      }
    ];
  }

  private processUsersDeviceData(totalsResponse: any, filters: any[]): Record<DeviceMetricId, DeviceMetricSlice[]> {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const totalsPayload = Array.isArray(totalsResponse.payload) ? totalsResponse.payload : [];
    const data = totalsPayload[0] || {};
    const totalUsers = parseInt((data?.['TNCIDs'] as string) || '0', 10) || 0;

    // Get selected filters from filter service
    const selectedDeviceIds = this.filterService.getSelectedDevices().map(d => d.id);
    const selectedSourceIds = this.filterService.getSelectedTrafficSources().map(s => s.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    // Filter columns based on selection
    // If nothing is selected, interpret as "all selected"
    let deviceColumns = filters.filter(f => f.ObjectType === 'device');
    if (selectedDeviceIds.length > 0) {
      deviceColumns = deviceColumns.filter(col => selectedDeviceIds.includes(col.ColumnName));
    }

    let sourceColumns = filters.filter(f => f.ObjectType === 'source');
    if (selectedSourceIds.length > 0) {
      sourceColumns = sourceColumns.filter(col => selectedSourceIds.includes(col.ColumnName));
    }

    let goalColumns = filters.filter(f => f.ObjectType === 'goal');
    if (selectedGoal) {
      goalColumns = goalColumns.filter(col => col.ColumnName === selectedGoal.id);
    }

    const buildSlices = (columns: any[], getColor: (col: any, index: number) => string): DeviceMetricSlice[] => {
      // First pass: get raw values, filter and sort
      const slicesWithRaw = columns
        .map((col, index) => {
          const raw = parseInt(data[col.ColumnName] || '0', 10) || 0;
          return {
            label: col.ObjectName,
            value: 0, // Will be recalculated
            raw,
            color: getColor(col, index),
            tooltip: '' // Will be recalculated
          };
        })
        .filter(slice => slice.raw > 0)
        .sort((a, b) => b.raw - a.raw)
        .slice(0, 6);

      // Calculate total volume from selected slices only
      const totalVolume = slicesWithRaw.reduce((sum, slice) => sum + slice.raw, 0);

      // Second pass: calculate percentages based on selected slices
      return slicesWithRaw.map(slice => {
        const percentage = totalVolume > 0 ? Math.round((slice.raw / totalVolume) * 100) : 0;
        return {
          ...slice,
          value: percentage,
          tooltip: `${slice.label}: ${this.formatNumber(slice.raw)} (${percentage}%)`
        };
      });
    };

    const deviceSlices = buildSlices(deviceColumns, (col) => this.getDeviceColor(col.ObjectName));
    const sourceSlices = buildSlices(sourceColumns, (_col, index) => this.metricPalette[index % this.metricPalette.length]);
    const goalSlices = buildSlices(goalColumns, (_col, index) => this.metricPalette[index % this.metricPalette.length]);

    return {
      device: deviceSlices,
      source: sourceSlices,
      goal: goalSlices
    };
  }

  private processTreemapData(
    totalsResponse: any,
    filters: any[],
    audienceIds: string[],
    audienceColors: Record<string, string> = {}
  ): any[] {
    if (totalsResponse.error) {
      throw new Error(totalsResponse.error.message);
    }

    const data = totalsResponse.payload?.[0] ?? {};
    const totalUsers = parseInt((data?.['TNCIDs'] as string) || '0', 10) || 0;

    const selectedAudienceIds = Array.isArray(audienceIds) ? audienceIds.slice(0, 6) : [];
    if (!selectedAudienceIds.length) {
      return [];
    }

    const acceptedTypes = new Set(['audience', 'personas']);
    const selectableColumns = (filters || []).filter(f =>
      acceptedTypes.has(f.ObjectType) && selectedAudienceIds.includes(f.ColumnName)
    );

    const columnById = new Map(selectableColumns.map(col => [col.ColumnName, col]));

    const audienceColorMap = this.filterService.getPublicAudiences().reduce((acc: Record<string, string>, audience, index) => {
      acc[audience.id] = audience.color || this.treemapColorPalette[index % this.treemapColorPalette.length];
      return acc;
    }, {});

    return selectedAudienceIds
      .map(audienceId => columnById.get(audienceId))
      .filter((audCol): audCol is typeof selectableColumns[number] => !!audCol)
      .map((audCol, index) => {
        const audUsers = parseInt(data[audCol.ColumnName] || '0', 10) || 0;
        const percentage = totalUsers > 0 ? Math.round((audUsers / totalUsers) * 100) : 0;
        const fallbackColor = this.treemapColorPalette[index % this.treemapColorPalette.length];

        return {
          id: audCol.ColumnName,
          name: audCol.ObjectName,
          value: percentage,
          user_count: audUsers,
          color: audienceColors?.[audCol.ColumnName] ||
            audienceColorMap[audCol.ColumnName] ||
            fallbackColor,
          label: `${this.formatNumber(audUsers)} | ${percentage}%`
        };
      })
      .filter(audience => audience.user_count > 0);
  }

  private processAudiencePublicData(dailyTrendResponse: any, audienceColumns: string[]): any[] {
    if (dailyTrendResponse.error) {
      throw new Error(dailyTrendResponse.error.message);
    }

    const dailyData = dailyTrendResponse.payload || [];

    return dailyData.map((d: any) => ({
      month: this.formatDate(d.Date),
      values: audienceColumns.map(col => d[col] || 0)
    }));
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }

  private formatDate(dateStr: {value: string} | string): string {
    const raw = typeof dateStr === 'string' ? dateStr : dateStr?.value;
    const date = new Date(raw);
    const day = date.getDate();
    const monthNames = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu',
                       'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  }

  private createTotalUsersChart(data: any[]) {
    const ctx = document.getElementById('totalUsersChart') as HTMLCanvasElement;
    this.totalUsersHasData = Array.isArray(data) && data.length > 0;

    if (!ctx) return;

    this.totalUsersChartInstance?.destroy();
    this.totalUsersChartInstance = null;

    if (!this.totalUsersHasData) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    const values = data.map(d => Number(d.users) || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const padding = (maxValue - minValue) * 0.1 || maxValue * 0.1 || 1;

    this.totalUsersChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.date),
        datasets: [{
          data: data.map(d => d.users),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 4,
          fill: false,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { display: false },
          y: {
            display: false,
            suggestedMin: minValue - padding,
            suggestedMax: maxValue + padding
          }
        }
      }
    });
  }

  private createUsersCategoryChart(data: any[]) {
    const ctx = document.getElementById('usersCategoryChart') as HTMLCanvasElement;
    if (!ctx) return;

    this.usersCategoryChartInstance?.destroy();
    this.usersCategoryChartInstance = null;

    if (!Array.isArray(data) || data.length === 0) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    this.usersCategoryChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: data.map(d => d.category),
        datasets: [{
          data: data.map(d => d.percentage),
          backgroundColor: ['#1e40af', '#1e40af', '#1e40af'],
          barThickness: 35,
          maxBarThickness: 40
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            display: false,
            grid: { display: false },
            border: { display: false }
          },
          y: {
            display: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { size: 11 },
              color: '#333'
            }
          }
        },
        layout: {
          padding: {
            left: 10,
            right: 10
          }
        }
      },
      plugins: [{
        id: 'categoryPercentages',
        afterDatasetsDraw: (chart: any) => {
          const ctx = chart.ctx;
          const dataset = chart.data.datasets[0];
          if (!dataset) {
            return;
          }

          ctx.save();
          ctx.font = 'bold 13px Gantari';
          ctx.fillStyle = '#ffffff';
          ctx.textBaseline = 'middle';

          const metadata = chart.getDatasetMeta(0);
          metadata.data.forEach((element: any, index: number) => {
            const value = dataset.data[index];
            if (value === undefined || value === null) {
              return;
            }
            const text = `${value}%`;
            const baseX = element.base ?? 0;
            const endX = element.x ?? baseX;
            const labelX = baseX + (endX - baseX) / 2;
            const labelY = element.y ?? 0;

            ctx.textAlign = 'center';
            ctx.fillText(text, labelX, labelY);
          });

          ctx.restore();
        }
      }]
    });
  }

  private createUsersDeviceChart(): void {
    const ctx = document.getElementById('usersDeviceChart') as HTMLCanvasElement;
    if (!ctx) {
      return;
    }

    const slices = this.usersDeviceDataByMetric[this.selectedDeviceMetric] || [];
    this.usersDeviceLegend = slices.map(slice => ({ label: slice.label, color: slice.color }));

    this.usersDeviceChartInstance?.destroy();
    this.usersDeviceChartInstance = null;

    if (!slices.length) {
      this.usersDeviceLegend = [];
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    const labelThreshold = 8;

    this.usersDeviceChartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: slices.map(slice => slice.label),
        datasets: [{
          data: slices.map(slice => slice.raw),
          backgroundColor: slices.map(slice => slice.color),
          borderWidth: 0,
          borderColor: 'transparent'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title: () => '',
              label: (context) => {
                const slice = slices[context.dataIndex];
                if (!slice) {
                  return '';
                }
                const lines = slice.label.split(' - ');
                lines.push(`${this.formatNumber(slice.raw)} (${slice.value}%)`);
                return lines;
              }
            }
          }
        },
        layout: {
          padding: 0
        },
        animation: {
          animateRotate: true
        }
      },
      plugins: [{
        id: 'percentageLabels',
        afterDatasetsDraw: (chart: any) => {
          const ctx = chart.ctx;
          ctx.save();
          ctx.font = 'bold 13px Gantari';
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          chart.data.datasets.forEach((dataset: any, i: number) => {
            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((element: any, index: number) => {
              // Use the pre-calculated percentage from slices instead of raw data
              const slice = slices[index];
              if (slice && slice.value >= labelThreshold) {
                const { x, y } = element.tooltipPosition(false);
                ctx.fillText(slice.value + '%', x, y);
              }
            });
          });
          ctx.restore();
        }
      }]
    });
  }

  private createUsersAudienceChart(data: any[]) {
    const ctx = document.getElementById('usersAudienceChart') as HTMLCanvasElement;
    if (!ctx) return;

    const audienceList = this.selectedAudiences;

    this.usersAudienceChartInstance?.destroy();
    this.usersAudienceChartInstance = null;

    if (!Array.isArray(data) || !data.length || !audienceList.length) {
      const context = ctx.getContext('2d');
      if (context) {
        context.clearRect(0, 0, ctx.width, ctx.height);
      }
      return;
    }

    const dataMatrix = data.map(d => d.values);
    const flatValues = dataMatrix.flat().filter(v => typeof v === 'number' && !isNaN(v)) as number[];
    let minValue = flatValues.length ? Math.min(...flatValues) : 0;
    let maxValue = flatValues.length ? Math.max(...flatValues) : 1;

    if (minValue === maxValue) {
      const padding = minValue === 0 ? 1 : Math.abs(minValue) * 0.1;
      minValue -= padding;
      maxValue += padding;
    }

    this.usersAudienceChartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.month),
        datasets: audienceList.map((audience, index) => ({
          label: audience.name,
          data: data.map(d => d.values[index]),
          borderColor: audience.color,
          backgroundColor: audience.color + '20',
          borderWidth: 4,
          fill: false,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 7
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: true,
              color: '#e5e5e5'
            },
            ticks: {
              font: { size: 11 },
              color: '#666'
            }
          },
          y: {
            display: true,
            grid: {
              display: true,
              color: '#e5e5e5'
            },
            ticks: {
              font: { size: 11 },
              color: '#666'
            },
            suggestedMin: minValue,
            suggestedMax: maxValue
          }
        }
      }
    });
  }

  private updateTotalUsersDisplay(totalUsersData: any) {
    // Update Total Users display value with data from API
    const totalUsersValue = document.querySelector('.total-users .value');
    const totalUsersPercentage = document.querySelector('.total-users .percentage') as HTMLElement;

    if (totalUsersValue && totalUsersData.value) {
      totalUsersValue.textContent = totalUsersData.value;
    }
    if (totalUsersPercentage) {
      if (totalUsersData.percentage && totalUsersData.percentage.trim() !== '') {
        // Show percentage when it has a value
        totalUsersPercentage.textContent = totalUsersData.percentage;
        totalUsersPercentage.style.display = 'inline'; // Make sure it's visible

        // Apply color based on growth type
        if (totalUsersData.growthType === 'positive') {
          totalUsersPercentage.style.color = '#10b981'; // Green
        } else if (totalUsersData.growthType === 'negative') {
          totalUsersPercentage.style.color = '#ef4444'; // Red
        } else {
          totalUsersPercentage.style.color = '#6b7280'; // Neutral grey
        }
      } else {
        // Hide percentage when it's empty
        totalUsersPercentage.style.display = 'none';
      }
    }
  }

  private updateTreemapData(treemapData: any[]) {
    const audiences = Array.isArray(treemapData) ? treemapData.slice(0, 6) : [];
    this.treemapHasData = audiences.length > 0;

    if (!this.treemapHasData) {
      this.audienceCards = [];
      return;
    }

    this.audienceCards = audiences.map((audience, index) => {
      const fallbackColor = this.treemapColorPalette[index % this.treemapColorPalette.length];
      const cardColor = audience.color || fallbackColor;

      return {
        name: audience.name,
        value: this.formatCardNumber(audience.user_count),
        percentage: `${audience.value}%`,
        color: cardColor,
        textColor: '#ffffff'
      };
    });
  }

  private formatCardNumber(num: number): string {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }

  private getContrastColor(hexColor: string): string {
    const hex = (hexColor || '').replace('#', '');
    if (hex.length !== 6) {
      return '#ffffff';
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#0f172a' : '#ffffff';
  }

  // Export functions
  exportToExcel() {
    if (!this.isTableView) {
      alert('Please switch to table view to export data');
      return;
    }

    const wsData: any[][] = [];

    // Add headers
    const headers = [];
    if (this.columnVisibility.audience) headers.push('Audience');
    if (this.columnVisibility.source) headers.push('Source');
    if (this.columnVisibility.device) headers.push('Device');
    if (this.columnVisibility.users) headers.push('Users');
    if (this.columnVisibility.goal) headers.push('Goal');

    wsData.push(headers);

    // Add data rows
    this.expandedRows.forEach(row => {
      const dataRow = [];
      if (this.columnVisibility.audience) dataRow.push(row.audience || '');
      if (this.columnVisibility.source) dataRow.push(row.source || '');
      if (this.columnVisibility.device) dataRow.push(row.device || '');
      if (this.columnVisibility.users) dataRow.push(row.userCount || 0);
      if (this.columnVisibility.goal) dataRow.push(row.goal || '');

      wsData.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Public Data');

    const fileName = `dashboard-data-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  exportToPNG() {
    const exportElement = this.isTableView ?
      document.querySelector('.table-container') as HTMLElement :
      document.querySelector('.dashboard-grid') as HTMLElement;

    if (!exportElement) {
      alert('Nothing to export');
      return;
    }

    html2canvas(exportElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true
    }).then(canvas => {
      canvas.toBlob(blob => {
        if (blob) {
          const fileName = `dashboard-${this.isTableView ? 'table' : 'charts'}-${new Date().toISOString().split('T')[0]}.png`;
          saveAs(blob, fileName);
        }
      });
    }).catch(error => {
      console.error('Error exporting to PNG:', error);
      alert('Error exporting to PNG. Please try again.');
    });
  }
}
